/* eslint-disable @typescript-eslint/unbound-method -- host members are plain functions; taking one off does not drop a receiver */
/* eslint-disable max-lines */

import { toast, ToastActionButton } from '@heidi/design-system/toast';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { trackTemplateGeneration } from '@heidi/templates/data';
import { isComposingEvent } from '@heidi/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useEditorState, type Editor } from '@tiptap/react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { useIntl } from 'react-intl';
import { toast as sonnerToast } from 'sonner';
import { Key } from 'ts-key-enum';
import type { z } from 'zod';
import { GET_TEMPLATE_CREATION_RECORD_BY_ID } from '../../../../../../contracts';
import { useMutateCreateLatestVersionControl } from '../../../../../../data';
import {
  activeTabAtom,
  hasInitializedFormAtom,
  isNavigationBlockDisabledAtom,
  selectedVersionControlIdAtomFamily,
  templateCreationRecordIdAtom,
} from '../../../../../template-builder/atoms';
import type { useTemplateFormSchema } from '../../../../../template-builder/hooks/forms/schemas';
import { useMutateDeleteVersionControl } from '../../../../../template-builder/hooks/mutations/useMutateDeleteVersionControl';
import { useQueryTemplateCreationRecordById } from '../../../../../template-builder/hooks/queries/useQueryTemplateCreationRecordById';
import { useHandleEditTemplate } from '../../../../../template-builder/hooks/useHandleEditTemplate';
import { useUserTemplatePermissions } from '../../../../../template-builder/hooks/useUserTemplatePermissions';
import {
  createStructureGenerationInput,
  routeTemplateGeneration,
} from '../../../../../template-builder/templateGenerationRouting';
import {
  exampleGenerationStreamStateAtomFamily,
  structureGenerationStreamStateAtomFamily,
} from '../../../../../template-streaming/atoms';
import {
  updateStreamStates,
  updateVersionControlInQueryData,
} from '../../../../../template-streaming/streamStateHelpers';
import { ERR_UNSUPPORTED_TEMPLATE_STRUCTURE_REQUEST } from '../../../../../template-streaming/templateStreamEnvelope';
import { TemplateStructureStreamError } from '../../../../../template-streaming/templateStructureStreamError';
import { StreamStatus } from '../../../../../template-streaming/types';
import { useIsGenerating } from '../../../../../template-streaming/useIsGenerating';
import { useMutateGenerateExampleFromStructure } from '../../../../../template-streaming/useMutateGenerateExampleFromStructure';
import { useMutateGenerateExampleViaChat } from '../../../../../template-streaming/useMutateGenerateExampleViaChat';
import { useMutateGenerateStructureFromExampleNote } from '../../../../../template-streaming/useMutateGenerateStructureFromExampleNote';
import { useMutateGenerateStructureViaChat } from '../../../../../template-streaming/useMutateGenerateStructureViaChat';
import type { TemplateDetailsDictationEditor } from '../../../../template-details-host';
import { useTemplateDetailsHost } from '../../../../template-details-host-context';
import { templateDetailsMessages } from '../../../../templateDetails.messages';

export const useHandleSend = ({ editor }: { editor: Editor | null }) => {
  const host = useTemplateDetailsHost();
  const simpleHtmlToMarkdown = host.htmlToMarkdown;
  const simpleMarkdownToHtml = host.markdownToHtml;

  const intl = useIntl();
  const { mutateAsync: generateStructureViaChat } = useMutateGenerateStructureViaChat();
  const { mutateAsync: generateExample } = useMutateGenerateExampleFromStructure();
  const { mutateAsync: generateExampleViaChat } = useMutateGenerateExampleViaChat();
  const { mutateAsync: generateStructureFromExampleNote } =
    useMutateGenerateStructureFromExampleNote();
  const isTextEmpty = useEditorState({
    editor,
    selector: ({ editor }) => {
      return editor?.getText().trim().length === 0;
    },
  });

  const { isGenerating } = useIsGenerating();

  const { mutateAsync: deleteVersionControl } = useMutateDeleteVersionControl();

  const form =
    useFormContext<
      z.infer<ReturnType<typeof useTemplateFormSchema>['TemplateFormSchema']>
    >();

  const isDisabled = useMemo(() => {
    return Boolean(isTextEmpty || isGenerating);
  }, [isTextEmpty, isGenerating]);

  const templateCreationRecordId = useAtomValue(templateCreationRecordIdAtom) ?? '';
  const selectedVersionControlId = useAtomValue(
    selectedVersionControlIdAtomFamily(templateCreationRecordId ?? '')
  );

  const { data: templateCreationRecord } = useQueryTemplateCreationRecordById({
    templateCreationRecordId,
    select: (data) => ({
      templateId: data.template_creation_record.template_id,
      versionControls: data.version_controls,
    }),
  });

  const setSelectedVersionControlId = useSetAtom(
    selectedVersionControlIdAtomFamily(templateCreationRecordId)
  );

  const activeTab = useAtomValue(activeTabAtom);

  const { handleEditCustomTemplateAndNavigate } = useHandleEditTemplate();
  const { shouldDisableTemplateEditor } = useUserTemplatePermissions();

  const setIsNavigationBlockDisabled = useSetAtom(isNavigationBlockDisabledAtom);
  const setHasInitializedForm = useSetAtom(hasInitializedFormAtom);

  const queryClient = useQueryClient();
  const store = useStore();

  const { mutateAsync: createLatestVersionControl } = useMutateCreateLatestVersionControl(
    {
      onMutateComplete: ({ optimisticVersionControlId }) => {
        // this is to show the previous content in optimistic version control for animation purpose
        const selectedVersionControl = templateCreationRecord?.versionControls.find(
          (vc) => vc.id === selectedVersionControlId
        );

        setSelectedVersionControlId(optimisticVersionControlId);

        const structureStreamStateAtom = structureGenerationStreamStateAtomFamily({
          templateCreationRecordId,
          versionControlId: optimisticVersionControlId,
        });
        const exampleStreamStateAtom = exampleGenerationStreamStateAtomFamily({
          templateCreationRecordId,
          versionControlId: optimisticVersionControlId,
        });

        updateVersionControlInQueryData({
          queryClient,
          queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
          versionControlId: optimisticVersionControlId,
          field: 'template_structure',
          updater: (currentValue) => {
            return selectedVersionControl?.template_structure ?? currentValue;
          },
        });

        updateVersionControlInQueryData({
          queryClient,
          queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
          versionControlId: optimisticVersionControlId,
          field: 'example_note',
          updater: (currentValue) => {
            return selectedVersionControl?.example_note ?? currentValue;
          },
        });

        updateStreamStates({
          store,
          atoms: [exampleStreamStateAtom, structureStreamStateAtom],
          status: StreamStatus.WAITING_START,
          animationType: 'chat',
        });
      },
    }
  );

  const createVersionControlAndSetupStreams = useCallback(
    async ({ markdown }: { markdown: string }) => {
      const selectedVersionControl = templateCreationRecord?.versionControls.find(
        (vc) => vc.id === selectedVersionControlId
      );

      // The mutation's onMutateComplete will set up stream states for the optimistic version control
      const newVersionControl = await createLatestVersionControl({
        data: { chat_query: markdown },
        templateCreationRecordId,
      });

      // Set up stream states for the new version control
      const structureStreamStateAtom = structureGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId: newVersionControl.id ?? '',
      });
      const exampleStreamStateAtom = exampleGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId: newVersionControl.id ?? '',
      });
      updateStreamStates({
        store,
        atoms: [exampleStreamStateAtom, structureStreamStateAtom],
        status: StreamStatus.WAITING_START,
        animationType: 'chat',
      });

      updateVersionControlInQueryData({
        queryClient,
        queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
        versionControlId: newVersionControl.id ?? '',
        field: 'template_structure',
        updater: (currentValue) => {
          return selectedVersionControl?.template_structure ?? currentValue;
        },
      });

      updateVersionControlInQueryData({
        queryClient,
        queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
        versionControlId: newVersionControl.id ?? '',
        field: 'example_note',
        updater: (currentValue) => {
          return selectedVersionControl?.example_note ?? currentValue;
        },
      });

      setSelectedVersionControlId(newVersionControl.id);

      return {
        newVersionControl,
      };
    },
    [
      createLatestVersionControl,
      queryClient,
      selectedVersionControlId,
      setSelectedVersionControlId,
      store,
      templateCreationRecord?.versionControls,
      templateCreationRecordId,
    ]
  );

  const generateStructureThenExample = useCallback(
    async ({
      markdown,
      verifyCompliance = false,
    }: {
      markdown: string;
      verifyCompliance?: boolean;
    }) => {
      try {
        const { newVersionControl } = await createVersionControlAndSetupStreams({
          markdown,
        });

        await generateStructureViaChat({
          target_version_control_id: newVersionControl.id,
          base_template_version_control_id: selectedVersionControlId ?? '',
          user_prompt: markdown,
          current_template_structure: form?.getValues('structure'),
          animationType: 'chat',
          verify_compliance: verifyCompliance,
        }).catch((error) => {
          if (error instanceof TemplateStructureStreamError) {
            throw error;
          }
          const errorToastId = toast({
            tone: 'destructive',
            title: intl.formatMessage(templateDetailsMessages.errorProblemSendingMessage),
            action: (
              <ToastActionButton
                tone="destructive"
                onClick={() => {
                  sonnerToast.dismiss(errorToastId);
                  void generateStructureThenExample(
                    createStructureGenerationInput({ markdown, verifyCompliance })
                  );
                }}
              >
                {intl.formatMessage(templateDetailsMessages.retry)}
              </ToastActionButton>
            ),
          });
          throw error; // Rethrow to prevent generateExample from running
        });

        await generateExample({
          template_creation_record_id: templateCreationRecordId ?? '',
          version_control_id: newVersionControl.id,
          animationType: 'chat',
        });
      } catch (error) {
        if (error instanceof TemplateStructureStreamError) {
          await deleteVersionControl({
            versionControlId: error.targetVersionControlId,
          });
          setSelectedVersionControlId(selectedVersionControlId);
          if (error.code === ERR_UNSUPPORTED_TEMPLATE_STRUCTURE_REQUEST) {
            toast({
              tone: 'destructive',
              title: intl.formatMessage(templateDetailsMessages.errorUnsupportedRequest),
            });
            return;
          }
        }
        logger.error('Failed to generate template via chat', sanitizeError(error));
        toast({
          tone: 'destructive',
          title: intl.formatMessage(templateDetailsMessages.errorGeneratingExample),
          description:
            error instanceof Error
              ? error.message
              : intl.formatMessage(templateDetailsMessages.errorUnknown),
        });
      }
    },
    [
      createVersionControlAndSetupStreams,
      deleteVersionControl,
      form,
      generateExample,
      generateStructureViaChat,
      selectedVersionControlId,
      setSelectedVersionControlId,
      intl,
      templateCreationRecordId,
    ]
  );

  const generateExampleThenStructure = useCallback(
    async ({ markdown }: { markdown: string }) => {
      try {
        const { newVersionControl } = await createVersionControlAndSetupStreams({
          markdown,
        });

        // 2. Update example via chat on the new version control
        const streamedExample = await generateExampleViaChat({
          template_creation_record_id: templateCreationRecordId,
          target_version_control_id: newVersionControl.id,
          current_example: form.getValues('example') ?? '',
          user_request: markdown,
          animationType: 'chat',
        });

        // 3. Generate structure from the updated example
        await generateStructureFromExampleNote({
          template_creation_record_id: templateCreationRecordId,
          target_version_control_id: newVersionControl.id,
          base_template_version_control_id: selectedVersionControlId ?? '',
          edited_example_note: streamedExample,
          current_template_structure: form.getValues('structure'),
          user_request: markdown,
          animationType: 'chat',
        });
      } catch (error) {
        logger.error(
          'Failed to generate template example then structure via chat',
          sanitizeError(error)
        );
        toast({
          tone: 'destructive',
          title: intl.formatMessage(templateDetailsMessages.errorProblemSendingMessage),
          description:
            error instanceof Error
              ? error.message
              : intl.formatMessage(templateDetailsMessages.errorUnknown),
        });
      }
    },
    [
      createVersionControlAndSetupStreams,
      form,
      selectedVersionControlId,
      intl,
      templateCreationRecordId,
      generateExampleViaChat,
      generateStructureFromExampleNote,
    ]
  );

  const handleSend = useCallback(
    async ({
      markdownContent,
      verifyCompliance = false,
      forceStructure = false,
    }: {
      markdownContent?: string;
      verifyCompliance?: boolean;
      forceStructure?: boolean;
    } = {}) => {
      if (shouldDisableTemplateEditor) {
        let markdown = simpleHtmlToMarkdown({ html: editor?.getHTML() ?? '' });
        // Remove trailing newlines from the editor
        if (editor && markdown.endsWith('\n')) {
          markdown = markdown.replace(/\n+$/, '');
          editor.commands.setContent(simpleMarkdownToHtml({ markdown }));
        }

        await handleEditCustomTemplateAndNavigate({
          exampleNote: form?.getValues('example') ?? '',
          onBeforeNavigate: () => {
            setIsNavigationBlockDisabled(true);
          },
          onAfterNavigate: () => {
            form.reset();
            setHasInitializedForm(false);
            setIsNavigationBlockDisabled(false);
          },
        });
        return;
      }

      trackTemplateGeneration({
        templateId: templateCreationRecord?.templateId ?? undefined,
        src: 'chat',
      });
      (editor as TemplateDetailsDictationEditor | null)?.commands.stopSpeechRecognition();
      const markdown =
        markdownContent ?? simpleHtmlToMarkdown({ html: editor?.getHTML() ?? '' });
      editor?.commands.clearContent();

      await routeTemplateGeneration({
        activeTab,
        forceStructure,
        markdown,
        verifyCompliance,
        generateStructureThenExample,
        generateExampleThenStructure,
      });
    },
    [
      shouldDisableTemplateEditor,
      simpleHtmlToMarkdown,
      simpleMarkdownToHtml,
      templateCreationRecord?.templateId,
      editor,
      activeTab,
      handleEditCustomTemplateAndNavigate,
      form,
      setIsNavigationBlockDisabled,
      setHasInitializedForm,
      generateStructureThenExample,
      generateExampleThenStructure,
    ]
  );

  useHotkeys(
    Key.Enter,
    () => {
      void handleSend();
    },
    {
      enabled: !isDisabled && !!editor,
      enableOnContentEditable: true,
      preventDefault: true,
      ignoreEventWhen: isComposingEvent,
    },
    [handleSend, isDisabled]
  );

  return {
    handleSend,
    isDisabled,
    isGenerating,
    shouldDisableTemplateEditor,
  };
};
